import { isArray, isEmpty, isNil } from 'lodash';

import {
  addCalendarDate,
  CalendarDate,
  formatCalendarMonthAndYear,
  isAfter,
  isAfterOrEqual,
  isBefore,
  isBeforeOrEqual,
  today,
  toCalendarDate,
  toCalendarDateNullable,
} from './dateutils';
import { LeaseExtended } from './leaseutils';
import { isNilOrEmpty } from './objectUtils';
import {
  Building,
  BuildingEvent,
  Language,
  Lease,
  LeaseDTO,
  LeaseStatus,
  ModelUnitLeaseConnection,
  Unit,
  UnitDTO,
  UnitEvent,
  UnitEventType,
  UnitLease,
} from './types';

type EventOrLease = Lease | UnitEvent | UnitLease;
type EventOrLeaseExtended = EventOrLease | LeaseExtended;

const isLease = (event: EventOrLease) => {
  return event.hasOwnProperty('initialRentalPrice');
};

const isUnitLease = (event: Lease | UnitLease | Omit<UnitEvent, 'unitId'> | BuildingEvent) => {
  return event.hasOwnProperty('lease');
};

export const getUnitsName = (units: UnitLease[] | null): string => {
  let unitsName = '';
  if (!isNil(units)) {
    (units as UnitLease[]).forEach((u) => {
      unitsName += (isEmpty(unitsName) ? '' : ', ') + (u.unit?.name ?? '');
    });
  }
  return unitsName;
};

export const getCurrentLeases = (leases: EventOrLeaseExtended[] | undefined | null): EventOrLeaseExtended[] | null => {
  return getLeasesDuringDate(leases, today());
};

export const getLeasesDuringDate = (
  leases: EventOrLease[] | undefined | null,
  date: CalendarDate
): EventOrLease[] | null => {
  return getLeasesDuringPeriod(leases, date, date);
};

const OCCUPYING_LEASE_STATUSES = [LeaseStatus.Active, LeaseStatus.Ended];

const PERCENT_BASE = 100;

export interface OccupationPeriod {
  startDate: CalendarDate;
  endDate: CalendarDate | null;
}

export const getOccupationPeriod = (lease: EventOrLease): OccupationPeriod | null => {
  const unitLease = isUnitLease(lease);
  const currentLease = unitLease ? (lease as UnitLease).lease : (lease as UnitEvent | Lease);
  if (isNilOrEmpty(currentLease)) {
    return null;
  }

  const leaseStatus = (currentLease as Lease).status;
  if (!isNil(leaseStatus) && !OCCUPYING_LEASE_STATUSES.includes(leaseStatus)) {
    return null;
  }

  const startDate = toCalendarDateNullable(lease.startDate) ?? toCalendarDateNullable(currentLease.startDate);
  if (isNil(startDate)) {
    return null;
  }

  return { startDate, endDate: toCalendarDateNullable(lease.endDate) ?? toCalendarDateNullable(currentLease.endDate) };
};

const overlapsPeriod = (
  periodFrom: CalendarDate,
  periodTo: CalendarDate,
  { startDate, endDate }: OccupationPeriod
): boolean => {
  if (isAfter(startDate, periodTo)) {
    return false;
  }
  return isNil(endDate) || isAfterOrEqual(endDate, periodFrom);
};

export const getLeasesDuringPeriod = (
  leasesModels: EventOrLease[] | ModelUnitLeaseConnection | undefined | null,
  periodFrom: CalendarDate,
  periodTo: CalendarDate
): EventOrLease[] | null => {
  if (isNil(leasesModels) || isEmpty(leasesModels)) {
    return null;
  }

  let leases = leasesModels;
  if ('items' in leasesModels) {
    if (!leasesModels.items || isEmpty(leasesModels.items)) {
      return null;
    }
    leases = (leasesModels as ModelUnitLeaseConnection).items?.filter((ul) => ul) as unknown as UnitLease[];
  }

  const currentLeases = (leases as (Lease | UnitLease | UnitEvent)[]).filter((lease: EventOrLease) => {
    const occupationPeriod = getOccupationPeriod(lease);
    if (isNil(occupationPeriod)) {
      return false;
    }

    return overlapsPeriod(periodFrom, periodTo, occupationPeriod);
  });
  if (isNil(currentLeases) || isEmpty(currentLeases)) {
    return null;
  }

  return currentLeases as Lease[] | (UnitEvent | UnitLease)[];
};

export const getNextOccupationStartDate = (
  leases: EventOrLease[] | undefined | null,
  fromDate: CalendarDate
): CalendarDate | null => {
  if (isNilOrEmpty(leases)) {
    return null;
  }

  return (leases as EventOrLease[]).reduce((earliestStartDate: CalendarDate | null, lease) => {
    const occupationPeriod = getOccupationPeriod(lease);
    if (isNil(occupationPeriod) || isBefore(occupationPeriod.startDate, fromDate)) {
      return earliestStartDate;
    }
    if (isNil(earliestStartDate) || isBefore(occupationPeriod.startDate, earliestStartDate)) {
      return occupationPeriod.startDate;
    }
    return earliestStartDate;
  }, null);
};

const isChainedTo = (occupationPeriod: OccupationPeriod, endDate: CalendarDate) =>
  isBeforeOrEqual(occupationPeriod.startDate, addCalendarDate(endDate, 1, 'days'));

const isChainedFrom = (occupationPeriod: OccupationPeriod, startDate: CalendarDate) =>
  !isNil(occupationPeriod.endDate) &&
  isAfterOrEqual(addCalendarDate(occupationPeriod.endDate, 1, 'days'), startDate) &&
  isBefore(occupationPeriod.startDate, startDate);

export const getMergedOccupationPeriod = (
  leases: EventOrLease[] | undefined | null,
  occupationPeriod: OccupationPeriod
): OccupationPeriod => {
  const occupationPeriods = (leases ?? []).reduce((accumulator: OccupationPeriod[], lease) => {
    const currentOccupationPeriod = getOccupationPeriod(lease);
    if (!isNil(currentOccupationPeriod)) {
      accumulator.push(currentOccupationPeriod);
    }
    return accumulator;
  }, []);

  let mergedPeriod = occupationPeriod;
  let hasMerged = true;
  while (hasMerged) {
    const { startDate, endDate } = mergedPeriod;
    const chainedTo = occupationPeriods.find(
      (currentPeriod) =>
        !isNil(endDate) &&
        isChainedTo(currentPeriod, endDate) &&
        (isNil(currentPeriod.endDate) || isAfter(currentPeriod.endDate, endDate))
    );
    const chainedFrom = occupationPeriods.find((currentPeriod) => isChainedFrom(currentPeriod, startDate));

    hasMerged = !isNil(chainedTo) || !isNil(chainedFrom);
    mergedPeriod = {
      startDate: chainedFrom?.startDate ?? startDate,
      endDate: isNil(chainedTo) ? endDate : chainedTo.endDate,
    };
  }

  return mergedPeriod;
};

export const getPreviousOccupationEndDate = (
  leases: EventOrLease[] | undefined | null,
  beforeDate: CalendarDate
): CalendarDate | null => {
  if (isNilOrEmpty(leases)) {
    return null;
  }

  return (leases as EventOrLease[]).reduce((latestEndDate: CalendarDate | null, lease) => {
    const occupationPeriod = getOccupationPeriod(lease);
    if (isNil(occupationPeriod) || isNil(occupationPeriod.endDate)) {
      return latestEndDate;
    }
    const { endDate } = occupationPeriod;
    if (isAfterOrEqual(endDate, beforeDate)) {
      return latestEndDate;
    }
    if (isNil(latestEndDate) || isAfter(endDate, latestEndDate)) {
      return endDate;
    }
    return latestEndDate;
  }, null);
};

export const getFirstCurrentLease = (leases: EventOrLease[] | undefined | null): EventOrLease | null => {
  const currentLeases = getCurrentLeases(leases);

  if (isNil(currentLeases) || isEmpty(currentLeases)) {
    return null;
  }

  return currentLeases[0];
};

export const getFirstLeaseDuringDate = (
  leases: EventOrLease[] | undefined | null,
  date: CalendarDate
): UnitLease | Lease | UnitEvent | null => {
  const currentLeases = getLeasesDuringDate(leases, date);

  if (isNil(currentLeases) || isEmpty(currentLeases)) {
    return null;
  }

  return currentLeases[0];
};

export const getFirstCurrentEvent = (events: UnitEvent[]) => {
  return getFirstCurrentLease(events);
};

export const getFutureLeases = (
  leases: Lease[] | (UnitEvent | UnitLease)[] | LeaseExtended[] | undefined | null
): EventOrLeaseExtended[] | null => {
  if (isNil(leases) || isEmpty(leases)) {
    return null;
  }

  const futurLeases = (leases as EventOrLeaseExtended[]).filter((lease) => {
    if (isUnitLease(lease)) {
      return (
        !isNil((lease as UnitLease).lease!.startDate) &&
        !isNil((lease as UnitLease).lease!.endDate) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.startDate), today()) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.endDate), today())
      );
    }
    const typedLease = lease as UnitEvent | Lease | LeaseExtended;
    return (
      !isNil(typedLease.startDate) &&
      !isNil(typedLease.endDate) &&
      isAfter(toCalendarDate(typedLease.startDate), today()) &&
      isAfter(toCalendarDate(typedLease.endDate), today())
    );
  });
  if (isNil(futurLeases) || isEmpty(futurLeases)) {
    return null;
  }

  return futurLeases;
};

export const hasFuturLease = (leases: Lease[] | (UnitEvent | UnitLease)[] | undefined | null): boolean => {
  if (isNil(leases) || isEmpty(leases)) {
    return false;
  }

  const futurLeases = (leases as EventOrLease[]).filter((lease) => {
    if (isUnitLease(lease)) {
      return (
        !isNil((lease as UnitLease).lease!.startDate) &&
        !isNil((lease as UnitLease).lease!.endDate) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.startDate), today()) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.endDate), today())
      );
    }
    const typedLease = lease as Lease | UnitEvent;
    return (
      !isNil(typedLease.startDate) &&
      !isNil(typedLease.endDate) &&
      isAfter(toCalendarDate(typedLease.startDate), today()) &&
      isAfter(toCalendarDate(typedLease.endDate), today())
    );
  });
  if (isNil(futurLeases) || isEmpty(futurLeases)) {
    return false;
  }

  return true;
};

export const hasFuturLeaseForRoomId = (leases: EventOrLease[] | undefined | null, roomId: string) => {
  if (isNil(leases) || isEmpty(leases)) {
    return false;
  }

  const futurLeases = leases.filter((lease) => {
    if (isUnitLease(lease)) {
      return (
        !isNil((lease as UnitLease).lease!.startDate) &&
        !isNil((lease as UnitLease).lease!.endDate) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.startDate), today()) &&
        isAfter(toCalendarDate((lease as UnitLease).lease!.endDate), today()) &&
        (lease as UnitLease).unit!.id === roomId
      );
    } else if (isLease(lease)) {
      const typedLease = lease as Lease;
      return (
        !isNil(typedLease.units) &&
        !isNil(
          typedLease.units.find(
            (unitLease) =>
              !isNil(unitLease.lease!.startDate) &&
              !isNil(unitLease.lease!.endDate) &&
              isAfter(toCalendarDate(unitLease.lease!.startDate), today()) &&
              isAfter(toCalendarDate(unitLease.lease!.endDate), today()) &&
              unitLease.unit!.id === roomId
          )
        )
      );
    }
    const typedLease = lease as UnitEvent;
    return (
      !isNil(typedLease.startDate) &&
      !isNil(typedLease.endDate) &&
      isAfter(toCalendarDate(typedLease.startDate), today()) &&
      isAfter(toCalendarDate(typedLease.endDate), today()) &&
      typedLease.unitId === roomId
    );
  });
  if (isNil(futurLeases) || isEmpty(futurLeases)) {
    return false;
  }

  return true;
};

export const getCurrentBalanceForFirstLease = (leases: Lease[]) => {
  const lease = getFirstCurrentLease(leases);
  if (isNil(lease)) {
    return null;
  }
  return (lease as Lease).balance;
};

export const getSortableCurrentBalanceForFirstLease = (unit: Unit) => {
  const lease = getFirstCurrentLease(unit.leases);
  if (isNil(lease)) {
    return null;
  }
  return (lease as UnitLease).lease!.balance;
};

export const getSortableCurrentEndDateForFirstLease = (unit: Unit) => {
  const lease = getFirstCurrentLease(unit.leases);
  if (isNil(lease)) {
    return null;
  }
  return (lease as UnitLease).lease!.endDate;
};

export const getOverlappingEvents = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  events: (UnitLease | UnitEvent | BuildingEvent | { startDate: string; endDate: string })[] | undefined | null
) => {
  if (isNil(startDate) || isNil(endDate) || isNil(events) || isEmpty(events)) {
    return [];
  }

  const eventsConcerned = events.filter((event) => {
    if (isUnitLease(event as UnitLease | Omit<UnitEvent, 'unitId'> | BuildingEvent)) {
      const evStart = (event as UnitLease).startDate;
      const evEnd = (event as UnitLease).endDate;
      return (
        !isNil(evStart) &&
        !isNil(evEnd) &&
        isAfter(endDate, startDate) &&
        isAfter(toCalendarDate(evEnd), toCalendarDate(evStart)) &&
        isBefore(startDate, toCalendarDate(evEnd)) &&
        isBefore(toCalendarDate(evStart), endDate)
      );
    }
    const typedEvent = event as UnitEvent | BuildingEvent | { startDate: string; endDate: string };
    return (
      !isNil(typedEvent.startDate) &&
      !isNil(typedEvent.endDate) &&
      isAfter(endDate, startDate) &&
      isAfter(toCalendarDate(typedEvent.endDate), toCalendarDate(typedEvent.startDate)) &&
      isBefore(startDate, toCalendarDate(typedEvent.endDate)) &&
      isBefore(toCalendarDate(typedEvent.startDate), endDate)
    );
  });
  return eventsConcerned;
};

export const getEventTimeWindowShort = (event: Lease | UnitEvent, language: Language) =>
  !isNil(event.endDate) ? formatCalendarMonthAndYear(toCalendarDate(event.endDate), language) : '...';

export const getEventType = (event: UnitLease | UnitEvent, roomEvents = false) => {
  if ('type' in event && (event.type === UnitEventType.OWN_USE || event.type === UnitEventType.WORK)) return event.type;
  else {
    if (roomEvents) return 'LEASE_ROOM';
    else return 'LEASE';
  }
};

export const isEventForRoomId = (event: UnitEvent | Lease, roomId: string) => {
  if (isLease(event)) {
    const typedLease = event as Lease;
    return !isNil(typedLease.units) && !isNil(typedLease.units.find((unitLease) => unitLease.unit!.id === roomId));
  }
  return (event as UnitEvent).unitId === roomId;
};

export const isPastEvent = (event: UnitLease | UnitEvent): boolean => {
  const todayDate = today();
  return isBefore(toCalendarDate(event.startDate), todayDate) && isBefore(toCalendarDate(event.endDate!), todayDate);
};

export const isPresentEvent = (event: UnitLease | UnitEvent): boolean => {
  const todayDate = today();
  return (
    (isNil(event.endDate) && isBefore(toCalendarDate(event.startDate), todayDate)) ||
    (isBefore(toCalendarDate(event.startDate), todayDate) && isAfter(toCalendarDate(event.endDate!), todayDate))
  );
};

export const isFutureEvent = (event: UnitLease | UnitEvent): boolean => {
  const todayDate = today();
  return (
    (isNil(event.endDate) && isAfter(toCalendarDate(event.startDate), todayDate)) ||
    (isAfter(toCalendarDate(event.startDate), todayDate) && isAfter(toCalendarDate(event.endDate!), todayDate))
  );
};

export const getUnitEvents = (unitId: string, getUnit: (id: string) => Unit | undefined) => {
  const unit = getUnit(unitId);
  if (isNil(unit)) return [];
  const leasesToReturn = unit.leases!.filter(
    (unitLease) => unitLease.lease!.status !== LeaseStatus.Draft && unitLease.lease?.status !== LeaseStatus.Cancelled
  );
  return [...leasesToReturn, ...unit.events!];
};

export const isUnitLeaseChargeAdjustable = (unit: Unit) => {
  if (!unit.leases) return false;
  return unit.leases.some((unitLease) => {
    return unitLease.allowChargeAdjustment;
  });
};

export const convertUnitsToFrontendType = (backendUnits: UnitDTO[]): Unit[] => {
  const unitsFrontend = backendUnits.reduce(
    (res: (Omit<UnitDTO, 'leases'> & { leases: UnitLease[] })[], currentUnit) => {
      res.push({
        ...currentUnit,
        leases:
          currentUnit.leases && currentUnit.leases.items
            ? (currentUnit.leases.items.filter((ul) => ul) as UnitLease[])
            : ([] as UnitLease[]),
      });
      return res;
    },
    []
  );
  return unitsFrontend as unknown as Unit[];
};

export const getMainUnit = (unitLeases: UnitLease[]): Unit | null | undefined => {
  return unitLeases.find((unitLease) => unitLease.mainUnit)?.unit;
};

export const filterUnitsByOwnerId = (buildingsWithUnits: Building[], units: Unit[], ownerId: string) => {
  const allUnits = buildingsWithUnits.reduce<Unit[]>((selectedUnits, building) => {
    const allBuildingOwnersId = building.owners?.map((buildingOwner) => buildingOwner.contactId);
    if (building.units && (building.jointOwnershipId === ownerId || allBuildingOwnersId?.includes(ownerId))) {
      selectedUnits.push(...building.units);
    }
    return selectedUnits;
  }, []);

  units.forEach((unit) => {
    const allUnitOwnersId = unit.owners?.map((unitOwner) => unitOwner.ownerId);
    if (unit.jointOwnershipId === ownerId || allUnitOwnersId?.includes(ownerId)) {
      allUnits.push(unit);
    }
  });
  return allUnits;
};

export const getMainUnitId = (lease: Lease | LeaseExtended | LeaseDTO) => {
  const unitLeases = isArray(lease.units) ? lease.units : (lease as LeaseDTO).units!.items;
  return unitLeases?.find((unitLease) => unitLease?.mainUnit)?.unitId;
};

export const attachUnitLeaseRelations = (
  unitLeases: UnitLease[],
  leasesById: Map<string, Lease>,
  unitsById: Map<string, Unit>
): UnitLease[] =>
  unitLeases.map((unitLease) => ({
    ...unitLease,
    lease: leasesById.get(unitLease.leaseId),
    unit: unitsById.get(unitLease.unitId),
  }));

export const getOccupiedUnitsCount = (
  units: Unit[],
  periodFrom: CalendarDate = today(),
  periodTo: CalendarDate = periodFrom
): number =>
  units.filter(
    (unit) =>
      !isNilOrEmpty(getLeasesDuringPeriod([...(unit.leases ?? []), ...(unit.events ?? [])], periodFrom, periodTo))
  ).length;

export const getOccupancy = (
  units: Unit[],
  periodFrom: CalendarDate = today(),
  periodTo: CalendarDate = periodFrom
): number => {
  if (isNilOrEmpty(units)) {
    return 0;
  }
  return Math.ceil((getOccupiedUnitsCount(units, periodFrom, periodTo) / units.length) * PERCENT_BASE);
};
